/**
 * Centralized Initialization Service
 * 
 * Handles all app initialization logic including:
 * - Font loading (static fallbacks + dynamic Google Fonts)
 * - Config loading
 * - Session validation
 * - Analytics setup
 * - Resource prefetching
 */

import { useFonts } from 'expo-font';
import { useEffect, useState, useCallback } from 'react';
import { Image } from 'react-native';
import trackingService from './analytics/TrackingService';
import useAuthSession from '../app/hooks/custom/useAuthSession';
import { useStoreConfig } from '../contexts/StoreConfigContext';
import { injectDynamicManifest } from '../utils/manifestGenerator';
import { appConfigStore } from '../app/stores/AppConfig';
import { fontService } from './FontService';
import { shouldUseDarkMode, subscribeToThemeChanges } from '../utils/themeUtils';

interface InitializationState {
    fontsLoaded: boolean;
    configLoaded: boolean;
    sessionChecked: boolean;
    resourcesPrefetched: boolean;
    isReady: boolean;
}

export const useAppInitialization = () => {
    const [initState, setInitState] = useState<InitializationState>({
        fontsLoaded: false,
        configLoaded: false,
        sessionChecked: false,
        resourcesPrefetched: false,
        isReady: false,
    });

    const { checkSession } = useAuthSession();
    const { config, isLoading: configLoading } = useStoreConfig();
    
    // Load fonts
    const [fontsLoaded] = useFonts({
        primaryFont: require('../assets/fonts/neuehaas-roman.ttf'),
        logoFont: require('../assets/fonts/stalinist-one.ttf'),
    });

    // Track font loading
    useEffect(() => {
        if (fontsLoaded && !initState.fontsLoaded) {
            setInitState(prev => ({ ...prev, fontsLoaded: true }));
        }
    }, [fontsLoaded, initState.fontsLoaded]);

    // Track config loading and apply theme mode
    useEffect(() => {
        if (!configLoading && !initState.configLoaded) {
            setInitState(prev => ({ ...prev, configLoaded: true }));
            
            // Apply theme mode from store config
            if (config?.themeMode) {
                const themeMode = config.themeMode;
                
                // For 'dark' or 'light' modes: Always enforce the store's theme
                if (themeMode === 'dark') {
                    appConfigStore.getState().setDarkMode(true);
                } else if (themeMode === 'light') {
                    appConfigStore.getState().setDarkMode(false);
                }
                // For 'dynamic': Only set initial value if this is a fresh load (no persisted preference)
                // The persisted darkMode will be used if available, otherwise use device preference
                else if (themeMode === 'dynamic') {
                    const isHydrated = appConfigStore.getState().isConfigHydrated;
                    if (!isHydrated) {
                        const isDarkMode = shouldUseDarkMode('dynamic');
                        appConfigStore.getState().setDarkMode(isDarkMode);
                    }
                    // If hydrated, keep persisted darkMode (user's choice)
                }
            }
        }
    }, [configLoading, config, initState.configLoaded]);

    // Subscribe to device theme changes when in 'dynamic' mode
    useEffect(() => {
        if (!config?.themeMode) return;

        const unsubscribe = subscribeToThemeChanges(config.themeMode, (isDark) => {
            appConfigStore.getState().setDarkMode(isDark);
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [config?.themeMode]);

    // Load dynamic Google Fonts from config (only if not null - null means use local fonts)
    useEffect(() => {
        const loadConfiguredFonts = async () => {
            const fontsToLoad = [
                { name: config?.primaryFont, type: 'primaryFont' },
                { name: config?.secondaryFont, type: 'secondaryFont' },
                { name: config?.logoFont, type: 'logoFont' },
                { name: config?.headerOverlay?.font, type: 'headerOverlayFont' }
            ];

            for (const font of fontsToLoad) {
                // Only load if font is set (not null) - null means use local font
                if (font.name) {
                    try {
                        await fontService.loadFont(font.name);
                    } catch (error) {
                        console.error(`❌ [InitService] Failed to load ${font.type}: ${font.name}`, error);
                    }
                }
            }
        };

        if (!configLoading) {
            loadConfiguredFonts();
        }
    }, [configLoading, config?.primaryFont, config?.secondaryFont, config?.logoFont, config?.headerOverlay?.font]);

    // Prefetch resources based on config
    const prefetchResources = useCallback(async () => {
        if (!config) return;

        try {
            // Prefetch header image if available (skip videos - they stream)
            if (config.appHeaderImage && config.appHeaderMediaType !== 'video') {
                const isCustomImage = typeof config.appHeaderImage === 'string' && 
                    (config.appHeaderImage.startsWith('http://') || config.appHeaderImage.startsWith('https://'));
                
                if (isCustomImage) {
                    await Image.prefetch(config.appHeaderImage);
                }
            }

            // Prefetch app logo if it's a URL (skip data URLs - they're already embedded)
            if (config.appLogo && typeof config.appLogo === 'string' &&
                (config.appLogo.startsWith('http://') || config.appLogo.startsWith('https://')) &&
                !config.appLogo.startsWith('data:')) {
                await Image.prefetch(config.appLogo);
            }

            setInitState(prev => ({ ...prev, resourcesPrefetched: true }));
        } catch (error) {
            console.error('❌ [InitService] Resource prefetch failed:', error);
            // Don't block app load on prefetch failure
            setInitState(prev => ({ ...prev, resourcesPrefetched: true }));
        }
    }, [config]);

    // Main initialization sequence
    useEffect(() => {
        if (fontsLoaded && !configLoading && !initState.sessionChecked) {
            // Check session
            checkSession();
            setInitState(prev => ({ ...prev, sessionChecked: true }));

            // Initialize tracking service
            trackingService.initialize().catch(error => {
                console.error('❌ Tracking initialization failed:', error);
            });

            // Prefetch resources
            prefetchResources();
        }
    }, [fontsLoaded, configLoading, checkSession, initState.sessionChecked, prefetchResources]);

    // Mark app as ready when all critical resources are loaded
    useEffect(() => {
        if (fontsLoaded && !configLoading && initState.sessionChecked && !initState.isReady) {
            setInitState(prev => ({ ...prev, isReady: true }));

            // Inject dynamic PWA manifest
            injectDynamicManifest();
        }
    }, [fontsLoaded, configLoading, initState.sessionChecked, initState.isReady]);

    return {
        ...initState,
        config,
        configLoading,
    };
};

/**
 * Hook to track component mounting for debugging
 * Enable this in development if you need to debug mount/unmount cycles
 */
export const useComponentMount = (componentName: string) => {
    // Disabled for production - uncomment below for debugging
    // useEffect(() => {
    //     console.log(`🔵 [Mount] ${componentName} mounted`);
    //     return () => {
    //         console.log(`🔴 [Unmount] ${componentName} unmounted`);
    //     };
    // }, [componentName]);
};
